import { useNavigate, type NavigateFunction } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useActivities } from '../../hooks/useDashboard';
import type { ActivityItem } from '../../services/dashboardService';
import { usePanelContext, type DetailPanelInfo } from '../../contexts/PanelContext';
import { fetchTestCase } from '../../services/testCaseService';
import Spinner from '../ui/Spinner';

const ACTION_ICONS: Record<string, { icon: string; color: string }> = {
  ISSUE_CREATED: { icon: '🔵', color: 'border-blue-400' },
  ISSUE_UPDATED: { icon: '🟡', color: 'border-yellow-400' },
  ISSUE_TRANSITIONED: { icon: '🔵', color: 'border-blue-400' },
  ISSUE_DELETED: { icon: '🔴', color: 'border-red-400' },
  ISSUE_COMMENTED: { icon: '💬', color: 'border-gray-400' },
  TESTCASE_EXECUTED: { icon: '🟢', color: 'border-green-400' },
  TESTCASE_FAILED: { icon: '🔴', color: 'border-red-400' },
  REQUIREMENT_CREATED: { icon: '📋', color: 'border-blue-400' },
  SPEC_CREATED: { icon: '📄', color: 'border-blue-400' },
};

// CHG-157 REQ-EL-006: Tier 1~4 전체 액션 한글 라벨 (원본 enum 문자열 노출 제거)
const ACTION_LABELS: Record<string, string> = {
  // --- 기존 유지 ---
  ISSUE_CREATED: '작업 생성',
  ISSUE_UPDATED: '작업 수정',
  ISSUE_TRANSITIONED: '상태 변경',
  ISSUE_DELETED: '작업 삭제',
  ISSUE_COMMENTED: '코멘트 추가',
  ISSUE_MOVED: '작업 이동', // 기존에 누락되어 있었음 — 추가
  DESIGN_RE_REVIEW_FLAGGED: '재검토 필요 플래그', // 기존에 누락 — 추가

  // --- Requirement (CHG-157 핵심) ---
  REQUIREMENT_CREATED: '요구사항 생성',
  REQUIREMENT_UPDATED: '요구사항 수정됨',
  REQUIREMENT_STATUS_CHANGED: '요구사항 상태 변경',
  REQUIREMENT_DELETED: '요구사항 삭제',

  // --- Specification (CHG-157 핵심) ---
  SPEC_CREATED: '기능 명세 생성',
  SPEC_UPDATED: '기능 명세 수정됨',
  SPEC_DELETED: '기능 명세 삭제',
  TESTCASES_GENERATED: 'TC 자동 생성',
  TESTCASES_IMPORTED: 'TC 가져오기',

  // --- TestCase (Phase C) ---
  TESTCASE_CREATED: 'TC 생성',
  TESTCASE_UPDATED: 'TC 수정',
  TESTCASE_DELETED: 'TC 삭제',
  TESTCASE_EXECUTED: 'TC 실행',
  TESTCASE_REVIEW_COMPLETED: 'TC 검토 완료',
  TESTCASE_MOVED: 'TC 이동',

  // --- DesignItem (Phase C) ---
  DESIGN_ITEM_CREATED: '디자인 아이템 생성',
  DESIGN_ITEM_UPDATED: '디자인 아이템 수정',
  DESIGN_ITEM_DELETED: '디자인 아이템 삭제',
  DESIGN_ITEM_STATUS_CHANGED: '디자인 상태 변경',
  DESIGN_LINK_CREATED: '디자인 연결 추가',
  DESIGN_LINK_DELETED: '디자인 연결 해제',
  DESIGN_HANDOFF: '디자인 핸드오프',
  DESIGN_CHANGE_NOTE: '디자인 변경 노트',
  DESIGN_RE_REVIEW_DISMISSED: '재검토 해제',
  DESIGN_VERSION_PINNED: '디자인 버전 고정',
  DESIGN_VERSION_UNPINNED: '디자인 버전 고정 해제',
  DESIGN_REVIEW_REQUESTED: '디자인 리뷰 요청',
  DESIGN_REVIEW_SUBMITTED: '디자인 리뷰 제출',

  // --- TestRun (Phase C) ---
  TESTRUN_CREATED: '테스트 실행 생성',
  TESTRUN_STATUS_CHANGED: '테스트 실행 상태 변경',
  ISSUE_REOPENED: '작업 재오픈', // TC 실패로 인한 자동 재오픈 (testRunResultService)

  // --- GitHub 연동 (entityType=Issue, CHG-154로 이미 클릭 이동됨 — 라벨만 필요) ---
  GITHUB_BRANCH_CREATED: 'GitHub 브랜치 생성',
  GITHUB_PR_CREATED: 'GitHub PR 생성',
  GITHUB_COMMIT_LINKED: 'GitHub 커밋 연결',
  GITHUB_TRANSITION_FAILED: 'GitHub 상태 연동 실패',
  GITHUB_DEPLOYMENT: 'GitHub 배포',
  AUTOMATION_COMMENT: '자동화 코멘트',
  AUTOMATION_ASSIGN: '자동화 담당자 지정',

  // --- Tier 4 (클릭 불가, 라벨만) ---
  NOTIFICATION_SLA_VIOLATION: 'SLA 위반 알림',
  NOTIFICATION_REVIEW_REMINDER: '리뷰 리마인더',
  FIGMA_FILE_UPDATED: 'Figma 파일 업데이트',
  FIGMA_VERSION_CREATED: 'Figma 버전 생성',
  FIGMA_DEV_MODE_READY: 'Figma 개발 모드 준비',
  FIGMA_DEV_MODE_COMPLETED: 'Figma 개발 모드 완료',
  GITHUB_RELEASE: 'GitHub 릴리스',
};

// CHG-157: webhookHandlerService.ts에서 런타임에 동적으로 조합되는 'GITHUB_PR_' + action
// 액션 문자열은 정적 맵으로 커버 불가 — 최소한 사람이 읽을 수 있는 형태로 폴백
function getActionLabel(action: string): string {
  return ACTION_LABELS[action]
    ?? (action.startsWith('GITHUB_PR_') ? `GitHub PR ${action.replace('GITHUB_PR_', '')}` : action);
}

// CHG-157 REQ-EL-007: entityType별 클릭 이동 함수 테이블 (Issue/Comment는 item.issue로 별도 처리)
type OpenDetailPanel = (type: DetailPanelInfo['type'], itemId: string, metadata?: unknown) => void;
type NavigatorParams = {
  navigate: NavigateFunction;
  projectKey: string;
  projectId: string;
  item: ActivityItem;
  openDetailPanel: OpenDetailPanel;
};
type Navigator = (params: NavigatorParams) => void | Promise<void>;

const ENTITY_NAVIGATORS: Record<string, Navigator> = {
  Requirement: ({ navigate, projectKey, item }) =>
    navigate(`/projects/${projectKey}/requirements?panel=${item.entityId}&panelType=requirement`),
  Specification: ({ navigate, projectKey, item }) =>
    navigate(`/projects/${projectKey}/specs/${item.entityId}`),
  // CHG-157 Phase C
  // TestCase 사이드 패널은 metadata(TestCase 전체 객체)가 있어야 렌더링되고
  // metadata는 URL로 직렬화되지 않음(PanelContext.tsx L76) → 조회 후 openDetailPanel로 state 직접 주입.
  TestCase: async ({ navigate, projectKey, projectId, item, openDetailPanel }) => {
    const tc = await fetchTestCase(projectId, item.entityId);
    openDetailPanel('testcase', tc.id, tc);
    navigate(`/projects/${projectKey}/tests`);
  },
  DesignItem: ({ navigate, projectKey, item }) =>
    navigate(`/projects/${projectKey}/design-items/${item.entityId}`),
  TestRun: ({ navigate, projectKey, item }) => {
    // specId는 target 응답에서 조인되어 옴 (activityTargetResolver.ts resolveTestRuns)
    if (!item.target?.specId) return;
    navigate(`/projects/${projectKey}/specs/${item.target.specId}/test-runs/${item.entityId}`);
  },
};

function getClickHandler(
  item: ActivityItem,
  projectKey: string,
  projectId: string,
  navigate: NavigateFunction,
  openDetailPanel: OpenDetailPanel,
): (() => void) | null {
  // CHG-154 로직 유지 — Issue/Comment는 item.issue 기반이 최우선
  if (item.issue) {
    return () => navigate(`/projects/${projectKey}/issues/${item.issue!.id}`);
  }
  // 그 외 entityType은 target이 있고 소프트 삭제되지 않은 경우만 클릭 가능(404 방지)
  const navigator = ENTITY_NAVIGATORS[item.entityType];
  if (navigator && item.target && !item.target.deletedAt) {
    return () => {
      Promise.resolve(navigator({ navigate, projectKey, projectId, item, openDetailPanel })).catch(() => {
        toast.error('상세 정보를 불러오지 못했습니다.');
      });
    };
  }
  return null;
}

interface Props {
  projectId: string;
  projectKey: string;
}

export default function RecentActivityWidget({ projectId, projectKey }: Props) {
  const navigate = useNavigate();
  const { openDetailPanel } = usePanelContext();
  const { data, isLoading } = useActivities(projectId, 15);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><Spinner className="size-6" /></div>;
  }

  return (
    <div className="h-full flex flex-col">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">최근 활동</h3>
      {(!data || data.length === 0) ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">활동 없음</div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-0 pr-1">
          {data.map((item) => {
            const ai = ACTION_ICONS[item.action] ?? { icon: '⚪', color: 'border-gray-300' };
            const time = new Date(item.createdAt);
            const yy = String(time.getFullYear()).slice(2);
            const mm = String(time.getMonth() + 1).padStart(2, '0');
            const dd = String(time.getDate()).padStart(2, '0');
            const hh = String(time.getHours()).padStart(2, '0');
            const mi = String(time.getMinutes()).padStart(2, '0');
            const timeStr = `${yy}-${mm}-${dd} ${hh}:${mi}`;

            // 상태 전이 상세 정보 (CHG-154: 서버가 배치 변환한 상태명 사용, 없으면 라벨 폴백)
            let detail = getActionLabel(item.action);
            if (item.action === 'ISSUE_TRANSITIONED' && item.statusLabel) {
              const beforeLabel = item.statusLabel.before ?? '(알 수 없음)';
              const afterLabel = item.statusLabel.after ?? '(알 수 없음)';
              detail = `${beforeLabel} → ${afterLabel}`;
            }
            // 대상 표시 (CHG-154: issue 우선, CHG-157: 없으면 리졸버 target으로 확장)
            const entityKey = item.issue?.issueId ?? item.target?.key ?? '';
            const entityTitle = item.issue?.title ?? item.target?.title;
            const clickHandler = getClickHandler(item, projectKey, projectId, navigate, openDetailPanel);
            const clickable = !!clickHandler;

            return (
              <div
                key={item.id}
                onClick={clickHandler ?? undefined}
                className={`flex items-start gap-2 py-1.5 border-b border-gray-50 last:border-0 ${
                  clickable ? 'cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1' : ''
                }`}
              >
                <span className="text-sm flex-shrink-0 mt-0.5">{ai.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-800">
                    <span className="font-medium">{item.user.name}</span>
                    {entityKey && (
                      <span className="text-primary-600 font-mono ml-1" title={entityTitle}>{entityKey}</span>
                    )}
                    <span className="text-gray-500 ml-1">{detail}</span>
                  </div>
                </div>
                <span className="text-[11px] text-gray-400 flex-shrink-0">{timeStr}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
